/**
 * Single source of truth for all Email Sequence chips and messages (PRD §7).
 * Keeping these in one place means the list, detail, record panel and worklist
 * all show identical labels/colours.
 */
import { ChipProps } from "@mui/material";

export type SequenceModule = "POLICY" | "CUSTOMER" | "LEAD" | "CONTACT_LIST";

export type SequenceTriggerType =
  | "MANUAL"
  | "EMAIL_SENT"
  | "WORKFLOW"
  | "CRON_SQL"
  | "CONTACT_LIST";

export type SequenceStatus = "DRAFT" | "ACTIVE" | "INACTIVE";

export type EnrolmentStatus = "ACTIVE" | "PAUSED" | "COMPLETED" | "STOPPED";

// ---- Module ---------------------------------------------------------------
export const MODULE_LABELS: Record<SequenceModule, string> = {
  POLICY: "Policy",
  CUSTOMER: "Customer",
  LEAD: "Lead",
  CONTACT_LIST: "Contact list",
};

export const MODULE_OPTIONS = (
  Object.keys(MODULE_LABELS) as SequenceModule[]
).map((value) => ({ label: MODULE_LABELS[value], value }));

/** The record id column name per module (PRD §5.4). */
export const MODULE_ID_COLUMN: Record<SequenceModule, string> = {
  POLICY: "customer_policy_id",
  CUSTOMER: "customer_id",
  LEAD: "lead_id",
  CONTACT_LIST: "contact_list_code",
};

/** The record view used by the condition builder per module (PRD §5.2.B). */
export const MODULE_RECORD_VIEW: Record<SequenceModule, string> = {
  POLICY: "latest_all_customer_policy_details",
  CUSTOMER: "latest_customer_personal_information",
  LEAD: "latest_leads",
  CONTACT_LIST: "latest_sequence_contact_list",
};

// ---- Trigger --------------------------------------------------------------
export const TRIGGER_LABELS: Record<SequenceTriggerType, string> = {
  MANUAL: "Manual: staff add records",
  EMAIL_SENT: "When an email template is sent",
  WORKFLOW: "From a workflow",
  CRON_SQL: "Scheduled rule (advanced)",
  CONTACT_LIST: "Everyone in a contact list",
};

export const TRIGGER_SHORT_LABELS: Record<SequenceTriggerType, string> = {
  MANUAL: "Manual",
  EMAIL_SENT: "Email sent",
  WORKFLOW: "Workflow",
  CRON_SQL: "Scheduled rule",
  CONTACT_LIST: "Contact list",
};

// ---- Sequence status chip -------------------------------------------------
export const SEQUENCE_STATUS_CHIP: Record<
  SequenceStatus,
  { label: string; color: ChipProps["color"]; textColor?: string }
> = {
  DRAFT: { label: "Draft", color: "default", textColor: "#000" },
  ACTIVE: { label: "Active", color: "success" },
  INACTIVE: { label: "Inactive", color: "warning", textColor: "#000" },
};

// ---- Enrolment status chip ------------------------------------------------
export const ENROLMENT_STATUS_CHIP: Record<
  EnrolmentStatus,
  { label: string; color: ChipProps["color"]; textColor?: string }
> = {
  ACTIVE: { label: "Running", color: "success" },
  PAUSED: { label: "Paused", color: "warning", textColor: "#000" },
  COMPLETED: { label: "Completed", color: "info" },
  STOPPED: { label: "Stopped", color: "default", textColor: "#000" },
};

// ---- Stop reason ----------------------------------------------------------
export const STOP_REASON_LABELS: Record<string, string> = {
  replied: "Client replied",
  bounced: "Email bounced",
  exit_condition: "No longer qualifies",
  manual: "Stopped by user",
  error: "Stopped after repeated errors",
  record_missing: "Record no longer exists",
};

// ---- Step result ----------------------------------------------------------
export const STEP_RESULT_CHIP: Record<
  string,
  { label: string; color: ChipProps["color"]; textColor?: string }
> = {
  SENT: { label: "Sent", color: "success" },
  TASK_CREATED: { label: "Task created", color: "info" },
  SKIPPED: { label: "Skipped", color: "default", textColor: "#000" },
  FAILED: { label: "Failed (will retry)", color: "error" },
  CLAIMED: { label: "Running\u2026", color: "warning", textColor: "#000" },
};

// ---- Skip reason ----------------------------------------------------------
export const SKIP_REASON_LABELS: Record<string, string> = {
  replied_since_last_step: "Client replied",
  record_condition: "Condition matched",
  no_recipient: "No email address",
  anchor_null: "Date field is empty",
};

// ---- Enrolment step lifecycle state (UI PRD R2 §3) ------------------------
// One dot per step in the Progress column, coloured by state; also drives the
// expanded checklist icon colour.
export const ENROLMENT_STEP_STATE_STYLE: Record<
  string,
  { color: string; label: string }
> = {
  DONE: { color: "#2e7d32", label: "Done" }, // green
  FAILED: { color: "#d32f2f", label: "Failed" }, // red
  NEXT: { color: "#1976d2", label: "Next" }, // blue
  PENDING: { color: "#bdbdbd", label: "Pending" }, // grey
  NOT_RUN: { color: "#9e9e9e", label: "Not run" }, // struck-through grey
};

/** Skip reasons may arrive as `template_sent:<code>` so resolve the prefix. */
export const resolveSkipReasonLabel = (reason?: string | null): string => {
  if (!reason) return "Skipped";
  if (reason.startsWith("template_sent")) return "Template already sent";
  return SKIP_REASON_LABELS[reason] || reason;
};

// ---- Enrolment source -----------------------------------------------------
export const ENROLMENT_SOURCE_LABELS: Record<string, string> = {
  MANUAL: "Added by user",
  EMAIL_SENT: "Template sent",
  CRON_SQL: "Scheduled rule",
  WORKFLOW: "Workflow",
};

// ---- Enrol "not created" reasons ------------------------------------------
export const ENROL_NOT_CREATED_LABELS: Record<string, string> = {
  already_enrolled: "Already in this sequence",
  already_enrolled_this_cycle: "Already went through it this cycle",
  not_eligible: "Doesn't qualify (matches a stop condition)",
  record_missing: "Record not found",
  sequence_not_active: "Sequence isn't active",
  module_mismatch: "Wrong record type",
  no_steps: "Sequence has no steps",
};

// ---- Condition builder operants (PRD §5.2.B) ------------------------------
export const CONDITION_OPERANTS = [
  { label: "Equals", value: "EQUAL" },
  { label: "Not equal", value: "NOT_EQUAL" },
  { label: "Greater than", value: "GREATER" },
  { label: "Less than", value: "LESSER" },
  { label: "Greater than or equal", value: "GREATER_THAN_EQUAL" },
  { label: "Less than or equal", value: "LESSER_THAN_EQUAL" },
];

// ---- Operant → short phrase (used in the live "run when" sentence) --------
export const CONDITION_OPERANT_PHRASES: Record<string, string> = {
  EQUAL: "is",
  NOT_EQUAL: "is not",
  GREATER: "is greater than",
  LESSER: "is less than",
  GREATER_THAN_EQUAL: "is at least",
  LESSER_THAN_EQUAL: "is at most",
};

// ---- Policy date-field anchors (PRD §5.2.D "When") ------------------------
export const POLICY_ANCHOR_COLUMNS = [
  { label: "Expiry date", value: "expiry_date" },
  { label: "Effective date", value: "effective_date" },
  { label: "Cancellation date", value: "date_cancelled" },
];
